import React, { useState, useEffect } from 'react';
import { AlertTriangle, Check, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLES = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};
const LEVEL_LABELS = { high: 'Hoch', medium: 'Mittel', low: 'Niedrig' };

export const ChurnRiskSettingsCard = () => {
  const [settings, setSettings] = useState(null);
  const [scores, setScores] = useState([]);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadScores = async () => {
    try {
      const res = await axios.get(`${API}/settings/churn-risk/scores`, authHeaders());
      setScores(res.data.scores || []);
    } catch (e) {
      console.error('Error loading churn scores:', e);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/churn-risk`, authHeaders());
        setSettings(res.data);
        await loadScores();
      } catch (e) {
        console.error('Error loading churn risk settings:', e);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/churn-risk`, {
        enabled: !!settings.enabled,
        alert_enabled: !!settings.alert_enabled,
        high_threshold: Number(settings.high_threshold) || 60,
        medium_threshold: Number(settings.medium_threshold) || 35,
        alert_cooldown_days: Number(settings.alert_cooldown_days) || 14,
        sentiment_enabled: !!settings.sentiment_enabled,
      }, authHeaders());
      toast.success('Kündigungsrisiko-Einstellungen gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleAnalyzeNow = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/settings/churn-risk/analyze-now`, {}, authHeaders());
      const d = res.data;
      if (d.skipped === 'disabled') {
        toast.info('Kündigungsrisiko-Erkennung ist deaktiviert.');
      } else {
        toast.success(`Analyse fertig: ${d.flagged} auffällig, davon ${d.high_risk} mit hohem Risiko`);
      }
      await loadScores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analyse fehlgeschlagen');
    } finally {
      setAnalyzing(false);
    }
  };

  if (!settings) return null;

  return (
    <Card className="border-red-200" data-testid="churn-risk-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" />
              KI-Kündigungsrisiko-Erkennung
            </CardTitle>
            <CardDescription>
              Analysiert täglich um 08:30 alle Kunden auf Abwanderungs-Signale (Inaktivität,
              Stornos, sinkende Frequenz, offene Rechnungen, negative Chat-Stimmung).
              Bei hohem Risiko wirst du per WhatsApp & E-Mail alarmiert (Nummern & E-Mail aus der Admin-KI Karte).
            </CardDescription>
          </div>
          <Switch
            checked={!!settings.enabled}
            onCheckedChange={(v) => setSettings({ ...settings, enabled: !!v })}
            data-testid="churn-risk-toggle"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Schwelle „Hoch" (0–100)</Label>
            <Input
              data-testid="churn-risk-high-threshold"
              type="number" min="1" max="100"
              value={settings.high_threshold ?? 60}
              onChange={(e) => setSettings({ ...settings, high_threshold: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Schwelle „Mittel"</Label>
            <Input
              data-testid="churn-risk-medium-threshold"
              type="number" min="1" max="100"
              value={settings.medium_threshold ?? 35}
              onChange={(e) => setSettings({ ...settings, medium_threshold: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Alarm-Cooldown (Tage)</Label>
            <Input
              data-testid="churn-risk-cooldown"
              type="number" min="1"
              value={settings.alert_cooldown_days ?? 14}
              onChange={(e) => setSettings({ ...settings, alert_cooldown_days: e.target.value })}
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
          <div>
            <p className="text-sm font-medium">🚨 Admin-Alarm bei hohem Risiko</p>
            <p className="text-xs text-gray-600">
              WhatsApp + E-Mail mit Namen und Gründen — pro Kunde max. 1 Alarm im Cooldown-Zeitraum.
            </p>
          </div>
          <Switch
            checked={!!settings.alert_enabled}
            onCheckedChange={(v) => setSettings({ ...settings, alert_enabled: !!v })}
            data-testid="churn-risk-alert-toggle"
          />
        </div>

        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
          <div>
            <p className="text-sm font-medium">💬 Chat-Stimmungsanalyse (GPT)</p>
            <p className="text-xs text-gray-600">
              Prüft WhatsApp-Nachrichten auffälliger Kunden auf Unzufriedenheit & Kündigungsabsicht.
            </p>
          </div>
          <Switch
            checked={!!settings.sentiment_enabled}
            onCheckedChange={(v) => setSettings({ ...settings, sentiment_enabled: !!v })}
            data-testid="churn-risk-sentiment-toggle"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="churn-risk-save-btn">
            <Check className="h-4 w-4 mr-2" />
            {saving ? 'Speichert...' : 'Speichern'}
          </Button>
          <Button variant="outline" onClick={handleAnalyzeNow} disabled={analyzing} data-testid="churn-risk-analyze-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${analyzing ? 'animate-spin' : ''}`} />
            {analyzing ? 'Analysiert...' : 'Jetzt analysieren'}
          </Button>
        </div>

        {scores.length > 0 && (
          <div className="pt-3 border-t space-y-2" data-testid="churn-risk-scores-list">
            <Label>Aktuelle Risiko-Kunden ({scores.length})</Label>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {scores.map((s) => (
                <div key={s.customer_id} className="flex items-start justify-between gap-2 p-2 bg-gray-50 rounded-lg border" data-testid={`churn-risk-row-${s.customer_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{s.name}</p>
                    <p className="text-xs text-gray-600">{(s.reasons || []).join(' · ')}</p>
                  </div>
                  <Badge variant="outline" className={`shrink-0 ${LEVEL_STYLES[s.level] || ''}`}>
                    {LEVEL_LABELS[s.level] || s.level} · {s.score}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        {scores.length === 0 && (
          <p className="text-sm text-gray-500" data-testid="churn-risk-empty">
            ✅ Aktuell keine auffälligen Kunden — die Analyse läuft täglich automatisch.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
